import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import {
  Newspaper,
  Calendar,
  ArrowRight,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { Path } from "../../../../../static/Constants";
import { CommonButton } from "../../../../Components/ui";

const NewsDetailsSidebar = ({
  relatedNews = [],
  currentArticleId,
  formatPublishDate,
}) => {
  const history = useHistory();

  // Filter out current article and limit to top 4
  const displayRelated = relatedNews
    .filter((item) => String(item.id) !== String(currentArticleId))
    .slice(0, 4);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Related / Latest News Card */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 3.5,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <TrendingUp size={18} color={colors.primary.main} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.1rem" }}>
            Latest Stories
          </Typography>
        </Stack>

        {displayRelated.length > 0 ? (
          <Stack spacing={2} divider={<Divider sx={{ borderColor: colors.dashboard.tableBorder }} />}>
            {displayRelated.map((item) => {
              const displayDate = item.updatedOn || item.createdOn;

              return (
                <Box
                  key={item.id}
                  onClick={() => history.push(Path.getNewsDetails(item.id))}
                  sx={{
                    display: "flex",
                    gap: 1.75,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover .related-news-title": {
                      color: colors.primary.main,
                    },
                    "&:hover .related-news-img": {
                      transform: "scale(1.06)",
                    },
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: borderRadiusPx.md,
                      overflow: "hidden",
                      bgcolor: colors.slate[100],
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.imageUrl ? (
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt={item.title}
                        className="related-news-img"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    ) : (
                      <Newspaper size={24} color={colors.slate[400]} />
                    )}
                  </Box>

                  {/* Title & Date */}
                  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <Typography
                      className="related-news-title"
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: colors.slate[900],
                        fontSize: "0.9rem",
                        lineHeight: 1.35,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mb: 0.5,
                        transition: "color 0.2s ease",
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[400] }}>
                      <Calendar size={11} />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {formatPublishDate(displayDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: colors.slate[500] }}>
            No additional articles found.
          </Typography>
        )}

        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.dashboard.tableBorder}` }}>
          <CommonButton
            fullWidth
            variant="outline"
            size="small"
            endIcon={<ArrowRight size={14} />}
            onClick={() => history.push(Path.news)}
          >
            Explore All News
          </CommonButton>
        </Box>
      </Card>

      {/* Autobet Ecosystem CTA Card */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          background: colors.gradients.dark,
          color: colors.neutral.white,
          boxShadow: customShadows.card,
        }}
      >
        <Stack spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(255, 255, 255, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.primary.light,
            }}
          >
            <Sparkles size={18} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: colors.neutral.white, fontSize: "1.05rem" }}>
            Play Live Lotteries
          </Typography>
          <Typography variant="body2" sx={{ color: colors.slate[300], fontSize: "0.85rem", lineHeight: 1.5 }}>
            Try your luck with provably fair, decentralized Web3 games across MegaBET, SpinBET, MineBET, and RocketBET.
          </Typography>
          <Box sx={{ pt: 1 }}>
            <CommonButton
              fullWidth
              variant="primary"
              size="small"
              endIcon={<ArrowRight size={14} />}
              onClick={() => history.push(Path.allLotteries)}
            >
              View Active Pools
            </CommonButton>
          </Box>
        </Stack>
      </Card>
    </Box>
  );
};

NewsDetailsSidebar.propTypes = {
  relatedNews: PropTypes.array,
  currentArticleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formatPublishDate: PropTypes.func.isRequired,
};

export default NewsDetailsSidebar;
